<script setup>
import { computed, defineEmits, defineModel, defineProps, ref, watchEffect } from 'vue';
import FilterSvg from '@/components/UI/svg/FilterSvg.vue';
import CrossCloseNotifySvg from '@/components/UI/svg/CrossCloseNotifySvg.vue';
import ErrorTriangleSvg from '@/components/UI/svg/ErrorTriangleSvg.vue';
import BitrixEntityFilterForm from '@/components/CreateConnectionComponents/BitrixUpload/components/BitrixEntityFilterForm.vue';
import BitrixContactsFilterForm from '@/components/CreateConnectionComponents/BitrixUpload/components/BitrixContactsFilterForm.vue';

const isToggle = defineModel();
const directions = defineModel('directions', { default: () => [] });
const stages = defineModel('stages', { default: () => [] });
const conditionType = defineModel('conditionType', { default: 'AND' });
const conditions = defineModel('conditions', { default: () => [] });
const dateField = defineModel('dateField', { default: 'DATE_CREATE' });
const startDate = defineModel('startDate');
const endDate = defineModel('endDate');

const props = defineProps({
    dataType: {
        type: String,
        default: null,
    },
    dataTypeLabel: {
        type: String,
        default: '',
    },
    account: {
        type: [String, Object],
        default: null,
    },
});

const emits = defineEmits(['change-step', 'next-step']);

const isFilterValid = ref(false);
const errorMessage = ref('');

function getDate(date) {
    if (!date) {
        return null;
    }

    if (date.type === 'offset') {
        const result = new Date();
        const multiplier = date.offsetOperation?.type === 'MINUS' ? -1 : 1;

        if (date.offsetName?.type === 'MONTH') {
            result.setMonth(result.getMonth() + (date.length * multiplier));
            return result;
        }

        if (date.offsetName?.type === 'DAY') {
            result.setDate(result.getDate() + (date.length * multiplier));
            return result;
        }
    }

    if (date.type === 'exact') {
        return date.date ? new Date(date.date) : null;
    }

    return null;
}

function hasSelectedItems(items) {
    return Array.isArray(items) && items.length > 0;
}

function isDateRangeValid() {
    const start = getDate(startDate.value);
    const end = getDate(endDate.value);

    if (start === null || end === null) {
        errorMessage.value = 'Выберите период фильтрации';
        isFilterValid.value = false;
        return false;
    }

    if (end < start) {
        errorMessage.value = 'Дата окончания предшествует дате начала';
        isFilterValid.value = false;
        return false;
    }

    return true;
}

watchEffect(() => {
    if (props.dataType === 'LEADS') {
        if (!hasSelectedItems(stages.value)) {
            errorMessage.value = 'Выберите хотя бы одну стадию';
            isFilterValid.value = false;
            return;
        }

        if (!isDateRangeValid()) {
            return;
        }

        errorMessage.value = '';
        isFilterValid.value = true;
        return;
    }

    if (props.dataType === 'DEALS') {
        if (!hasSelectedItems(directions.value)) {
            errorMessage.value = 'Выберите хотя бы одно направление';
            isFilterValid.value = false;
            return;
        }

        if (!hasSelectedItems(stages.value)) {
            errorMessage.value = 'Выберите хотя бы одну стадию';
            isFilterValid.value = false;
            return;
        }

        if (!isDateRangeValid()) {
            return;
        }

        errorMessage.value = '';
        isFilterValid.value = true;
        return;
    }

    if (props.dataType === 'CONTACTS') {
        if (!isDateRangeValid()) {
            return;
        }

        errorMessage.value = '';
        isFilterValid.value = true;
        return;
    }

    errorMessage.value = '';
    isFilterValid.value = false;
});

const nextStepButtonClass = computed(() => {
    if (isFilterValid.value) {
        return 'bg-blue-500 hover:bg-blue-600 text-white cursor-pointer';
    }

    return 'bg-gray-400 text-gray-600 cursor-default';
});

function toggleBlock() {
    isToggle.value = false;
}

function changeStep() {
    emits('change-step');
}

function nextStep() {
    if (!isFilterValid.value) {
        return;
    }

    emits('next-step');
}
</script>

<template>
    <div class="relative">
        <div
            v-show="isToggle"
            class="w-full bg-white rounded-lg h-32 flex items-center justify-start shadow-xl border-2 border-transparent cursor-pointer hover:border-blue-500"
            @click="toggleBlock"
        >
            <div class="w-full flex items-center justify-between h-32">
                <div class="flex items-center justify-center">
                    <FilterSvg class="w-20 ml-5 mr-2" />
                    <div>
                        <div class="font-bold text-xl">
                            Фильтр Битрикс24
                        </div>
                        <div>
                            {{ dataTypeLabel || 'Условие' }}
                        </div>
                    </div>
                </div>
                <div
                    class="mr-10 size-10 flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer transition-all duration-150"
                    @click.stop="changeStep"
                >
                    <CrossCloseNotifySvg class="size-8" />
                </div>
            </div>
        </div>

        <div
            v-show="!isToggle"
            class="w-full flex flex-col justify-center items-start bg-white rounded-lg shadow-xl"
        >
            <div class="w-full flex items-center justify-between h-32">
                <div class="flex items-center justify-center">
                    <FilterSvg class="w-20 ml-5 mr-2" />
                    <div>
                        <div class="font-bold text-xl">
                            Фильтр Битрикс24
                        </div>
                        <div>
                            {{ dataTypeLabel || 'Условие' }}
                        </div>
                    </div>
                </div>
                <div
                    class="mr-10 size-10 flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer transition-all duration-150"
                    @click="changeStep"
                >
                    <CrossCloseNotifySvg class="size-8" />
                </div>
            </div>

            <BitrixEntityFilterForm
                v-if="dataType === 'DEALS'"
                entity-type="DEALS"
                title="Сделки"
                description="Настройте направление, стадии, период и дополнительные условия для выгрузки сделок."
                :account="account"
                :show-directions="true"
                :show-stages="true"
                v-model:directions="directions"
                v-model:stages="stages"
                v-model:condition-type="conditionType"
                v-model:conditions="conditions"
                v-model:date-field="dateField"
                v-model:start-date="startDate"
                v-model:end-date="endDate"
            />

            <BitrixEntityFilterForm
                v-else-if="dataType === 'LEADS'"
                entity-type="LEADS"
                title="Лиды"
                description="Настройте стадии, период и дополнительные условия для выгрузки лидов."
                :account="account"
                :show-directions="false"
                :show-stages="true"
                v-model:directions="directions"
                v-model:stages="stages"
                v-model:condition-type="conditionType"
                v-model:conditions="conditions"
                v-model:date-field="dateField"
                v-model:start-date="startDate"
                v-model:end-date="endDate"
            />

            <BitrixContactsFilterForm
                v-else-if="dataType === 'CONTACTS'"
                v-model:date-field="dateField"
                v-model:start-date="startDate"
                v-model:end-date="endDate"
            />

            <div
                v-else
                class="w-full px-6 pb-10"
            >
                <div class="text-base sm:text-lg font-bold text-gray-800 mb-2">
                    {{ dataTypeLabel }}
                </div>
                <p class="text-sm text-gray-500">
                    Здесь будет фильтрация данных Битрикс24 для выбранного типа выгрузки.
                </p>
            </div>

            <div
                v-show="!isFilterValid && errorMessage"
                class="flex items-center gap-5 px-6 pb-10"
            >
                <ErrorTriangleSvg class="size-6 stroke-white fill-red-500" />
                <div>
                    {{ errorMessage }}
                </div>
            </div>
        </div>

        <div
            class="fixed left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 md:right-6 lg:right-10 bottom-4 sm:bottom-5 px-5 py-3 rounded-full duration-150 transition-all select-none font-bold z-30"
            :class="nextStepButtonClass"
            @click="nextStep"
        >
            Следующий шаг
        </div>
    </div>
</template>
